// ============================================================================
// VOTING PARTICIPANTS TAB — WorkItemDetailModal 'voting_participants' tab.
// Hosts the voter roll editor — location-group scoping, eligibility, weights,
// check-in and per-voter ballot links — via the bespoke VotingRollEditor.
// ============================================================================

'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/total-operations/workflows/digital-voting/agenda-and-voter-roll.mdx
 * (published at user-docs.totalaccess.co.za/total-operations/workflows/digital-voting/agenda-and-voter-roll)
 * NOTE: headerless wrapper — the help icon lives on VotingRollEditor.
 */

import React from 'react';
import { Gavel, Loader2 } from 'lucide-react';
import type { WorkItem } from '@/components/UniversalWorkItems';
import VotingRollEditor from '@/app/client-zone/modules/total-operations/components/voting/VotingRollEditor';
import { useVotingSessionDetail } from './useVotingSessionDetail';

interface Props {
  workItem: WorkItem;
  isDark?: boolean;
}

export default function VotingParticipantsTab({ workItem, isDark = false }: Props) {
  const { detail, loading, error, reload } = useVotingSessionDetail(String(workItem.id));

  if (loading && !detail) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-20 text-center">
        <Gavel className={`h-10 w-10 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
        <p className="text-sm text-red-600">{error || 'Session not found'}</p>
      </div>
    );
  }

  return (
    <VotingRollEditor
      session={detail.session}
      quorum={detail.quorum}
      canManage={detail.can_manage}
      onChanged={() => void reload()}
    />
  );
}
